import type { QuotingPortal } from 'lib/hosts/quoting/interface';
import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export const ManhattanSubmitAction = 'manhattan-submit-quote';

const ManhattanCarrierPortalHost = 'mxcap1.sce.manh.com';
const ManhattanCarrierAppPathPrefix = '/sp/ui-carrier-app/';
export const ManhattanOpenBroadcastListPath =
  '/sp/ui-carrier-app/shipment-list/open/broadcast';
export const ManhattanBroadcastSpotDetailPathPattern =
  /^\/sp\/ui-carrier-app\/shipment-detail\/[^/]+\/[^/]+\/[^/]+\/broadcast_spot$/;

function normalizeManhattanPathname(pathname: string): string {
  return pathname.toLowerCase().replace(/\/+$/, '') || '/';
}

function isManhattanCarrierAppHost(hostname: string): boolean {
  return hostname.toLowerCase() === ManhattanCarrierPortalHost;
}

// True for HTTPS pages hosted by the supported Manhattan carrier portal.
export function isManhattanCarrierPortalHostUrl(
  urlString: Undef<string>
): boolean {
  if (!urlString) {
    return false;
  }

  try {
    const url = new URL(urlString);

    return url.protocol === 'https:' && isManhattanCarrierAppHost(url.hostname);
  } catch {
    return false;
  }
}

function isManhattanCarrierAppPath(pathname: string): boolean {
  return pathname.startsWith(ManhattanCarrierAppPathPrefix);
}

function isOpenBroadcastPath(pathname: string): boolean {
  if (pathname === ManhattanOpenBroadcastListPath) {
    return true;
  }

  return isBroadcastSpotDetailPath(pathname);
}

function isBroadcastSpotDetailPath(pathname: string): boolean {
  return ManhattanBroadcastSpotDetailPathPattern.test(pathname);
}

// True for Manhattan Active carrier-app URLs, including non-bidding routes.
export function isManhattanCarrierAppUrl(urlString: Undef<string>): boolean {
  if (!urlString) {
    return false;
  }

  try {
    const url = new URL(urlString);
    const pathname = normalizeManhattanPathname(url.pathname);

    return (
      isManhattanCarrierPortalHostUrl(urlString) &&
      isManhattanCarrierAppPath(pathname)
    );
  } catch {
    return false;
  }
}

// True for Open Broadcast list or BROADCAST_SPOT detail URLs.
// Used for portal recognition, opener visibility, and scraping.
export function isManhattanOpenBroadcastUrl(urlString: Undef<string>): boolean {
  if (!isManhattanCarrierAppUrl(urlString)) {
    return false;
  }

  try {
    return isOpenBroadcastPath(
      normalizeManhattanPathname(new URL(urlString!).pathname)
    );
  } catch {
    return false;
  }
}

// True for a Manhattan BROADCAST_SPOT shipment detail URL.
export function isManhattanBroadcastSpotDetailUrl(
  urlString: Undef<string>
): boolean {
  if (!isManhattanCarrierAppUrl(urlString)) {
    return false;
  }

  try {
    return isBroadcastSpotDetailPath(
      normalizeManhattanPathname(new URL(urlString!).pathname)
    );
  } catch {
    return false;
  }
}

// Detail pages render the bid-amount label immediately before .cost-input.
// The label text is "Spot (USD)" on Accept tenders and "Current Bid (USD)"
// on Submit Bid tenders.
export function hasManhattanDetailSpotUsdBidInput(html: string): boolean {
  if (!html) {
    return false;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const detailRoot = Array.from(
    doc.querySelectorAll('shipment-detail-page')
  ).find(
    (page) =>
      !page.classList.contains('ion-page-hidden') &&
      page.getAttribute('aria-hidden') !== 'true'
  );

  if (!detailRoot) {
    return false;
  }

  const costInputs = Array.from(detailRoot.querySelectorAll('.cost-input'));
  for (const costInput of costInputs) {
    const input = costInput.querySelector(
      'ion-input[data-component-id="locale-number-formatter"], ion-input, input'
    );
    const inputDisabled =
      input?.hasAttribute('disabled') ||
      input?.getAttribute('aria-disabled') === 'true';
    if (!input || inputDisabled) {
      continue;
    }

    const labelText = costInput.previousElementSibling?.textContent
      ?.replace(/\s+/g, ' ')
      .trim();
    if (labelText && /(spot|current\s*bid)\s*\(usd\)/i.test(labelText)) {
      return true;
    }
  }

  return false;
}

export class Manhattan implements QuotingPortal {
  submitAction = ManhattanSubmitAction;

  // Manhattan's tab URL can lag its live DOM during SPA navigation. Accept
  // either Open Broadcast route and let the active detail input determine
  // whether the Input buttons are available.
  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean {
    if (!tab?.url || !html) {
      return false;
    }

    try {
      if (!isManhattanOpenBroadcastUrl(tab.url)) {
        return false;
      }

      return hasManhattanDetailSpotUsdBidInput(html);
    } catch (error: unknown) {
      captureException(error, { context: 'Manhattan canSubmit' });
      return false;
    }
  }
}
